// hooks/useDebouncedSearch.ts
"use client";

import { useEffect, useRef, useState, useTransition } from "react";

type UseDebouncedSearchOptions = {
  delay?: number;
  onSearch: (value: string) => void;
};

export function useDebouncedSearch({
  delay = 300,
  onSearch,
}: UseDebouncedSearchOptions) {
  const [inputValue, setInputValue] = useState("");
  const [isPending, startTransition] = useTransition();
  const debounceRef = useRef<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // clear previous debounce
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    // debounce and call onSearch in a transition
    debounceRef.current = window.setTimeout(() => {
      startTransition(() => {
        onSearch(value);
      });
    }, delay);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    inputValue,
    onChange: handleChange,
    isPending,
  };
}
